// LooksReportsContext.js

import React, { createContext, useState, useEffect } from 'react';
import { doc, updateDoc }         from 'firebase/firestore';
import { db }                     from '../utils/firebaseConfig';
import { useAuth }                from './AuthContext';
import {
  getAllReportsFromDB,
  saveDataToDB
} from '../utils/dbUtilities';

export const LooksReportsContext = createContext();

/**
 * Provider for looksReports (the analogue of clipsReports).
 *  - Pulls every report once on mount               → reports[]
 *  - reportPost(...)  ⇢ saves a report for a post   → looksReports
 *                       **and** sets isReported=true on the post doc
 *  - reportUser(...)  ⇢ saves a report for a user   → looksReports
 *  - markReviewed(...)⇢ admin helper to clear/restore the flag
 */
export const LooksReportsProvider = ({ children }) => {
  const [reports, setReports] = useState([]);
  const { currentUser }       = useAuth();          // who is logged-in?

  /* ─────────────  initial load  ───────────── */
  useEffect(() => {
    (async () => {
      try {
        const fetched = await getAllReportsFromDB('looksReports');
        // newest first
        fetched.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        setReports(fetched);
      } catch (err) {
        console.error('LooksReportsContext: error fetching reports:', err);
      }
    })();
  }, []);

  /* ─────────────  helpers  ───────────── */
  const reportPost = async (reportDetails) => {
    if (!currentUser) {
      throw new Error('User must be logged in to report a post.');
    }

    const newReport = {
      ...reportDetails,            // must contain  postId
      type:      'post',
      userId:    currentUser.uid,  // reporter’s uid
      timestamp: new Date().toISOString()
    };

    try {
      // 1️⃣  Save the report itself
      const id = await saveDataToDB('looksReports', newReport);

      // 2️⃣  Flip the flag on the post so the UI can react immediately
      await updateDoc(doc(db, 'looksPosts', newReport.postId), { isReported: true });

      setReports((prev) => [...prev, { ...newReport, id }]);
      return id;
    } catch (err) {
      console.error('LooksReportsContext: error saving post report:', err);
      throw err;
    }
  };

  const reportUser = async (reportedUserId, details = {}) => {
    if (!currentUser) {
      throw new Error('User must be logged in to report another user.');
    }

    const newReport = {
      ...details,
      type:        'user',
      userId:      reportedUserId,   // user being reported
      reporterId:  currentUser.uid,  // reporter
      timestamp:   new Date().toISOString()
    };

    try {
      const id = await saveDataToDB('looksReports', newReport);
      setReports((prev) => [...prev, { ...newReport, id }]);
      return id;
    } catch (err) {
      console.error('LooksReportsContext: error saving user report:', err);
      throw err;
    }
  };

  /** Admin or moderator: clear / reset the flag on a post */
  const markReviewed = async (postId, isReported = false) => {
    try {
      await updateDoc(doc(db, 'looksPosts', postId), { isReported });
    } catch (err) {
      console.error('LooksReportsContext: error updating isReported flag:', err);
    }
  };

  /* ─────────────  context value  ───────────── */
  const value = { reports, reportPost, reportUser, markReviewed };

  return (
    <LooksReportsContext.Provider value={value}>
      {children}
    </LooksReportsContext.Provider>
  );
};

export default LooksReportsContext;
